/**
 * Trace-path context (material-genealogy-real F3) — the shared channel
 * between the trace surfaces (PartTraceView / BatchTraceView, FE-C) and the
 * process-graph canvas (FE-D). When the user hits "Show on graph" on a part
 * trace, the trace view calls `setActivePath(...)`; the canvas reads
 * `useTracePath()` and rings the on-path nodes / edges, dims the rest, and
 * shows a lane chip + per-step parameter chips. Pure presentation state —
 * no fetching, no persistence; cleared on drawer close.
 *
 * ── CRITICAL SHARED CONTRACT ───────────────────────────────────────────────
 * **`entity_instance_id` === `PlantNode.id`**. Every id in `activePath.nodeIds`
 * is a `PlantNode.id`, identical to the genealogy operation's
 * `entity_instance_id`. That single join key is what lets the canvas resolve a
 * trace step to a node. `paramsByNode` is keyed by the SAME `PlantNode.id`.
 * `edgeKeys` are `PlantEdge.id`s. Do NOT key this state by any other id space.
 * ───────────────────────────────────────────────────────────────────────────
 *
 * Mounted next to PlantProvider in the twin routes tree (App.tsx) so it spans
 * every /app/twin/* route and survives navigation between Parts → Process graph.
 */

import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  type ReactNode,
} from "react";

/** One captured parameter shown as a per-step chip on a highlighted node. */
export interface TracePathParam {
  name: string;
  value: number | string;
  unit?: string;
  /** Whether the value is within its declared spec range (drives chip color). */
  inSpec?: boolean;
}

/**
 * The real route an output took through the plant, ready to paint on the graph.
 * Built from `PartTrace.path` + the trace operations by the trace view.
 */
export interface ActiveTracePath {
  /** Ordered on-path nodes — `PlantNode.id`s (== operation `entity_instance_id`). */
  nodeIds: string[];
  /** Traversed edges — `PlantEdge.id`s. Optional (canvas can derive from nodes). */
  edgeKeys?: string[];
  /**
   * Per-step parameter chips, keyed by `PlantNode.id`. Lets the canvas annotate
   * each on-path node with the conditions captured there (pour temp, moisture …).
   */
  paramsByNode?: Record<string, TracePathParam[]>;
  /** Lane the route ran on — the specific furnace, for the lane chip. */
  furnaceId?: string;
  /** Lane the route ran on — the specific pour line, for the lane chip. */
  lineId?: string;
}

export interface TracePathState {
  /** The path to highlight, or `null` when nothing is pinned. */
  activePath: ActiveTracePath | null;
  /** Pin a path (replaces any current one). */
  setActivePath: (path: ActiveTracePath) => void;
  /** Clear the highlight (e.g. on drawer close). */
  clearActivePath: () => void;
}

const TracePathContext = createContext<TracePathState | null>(null);

export function TracePathProvider({ children }: { children: ReactNode }) {
  const [activePath, setActivePathState] = useState<ActiveTracePath | null>(
    null,
  );

  const setActivePath = useCallback((path: ActiveTracePath) => {
    setActivePathState(path);
  }, []);

  const clearActivePath = useCallback(() => {
    setActivePathState(null);
  }, []);

  const value = useMemo<TracePathState>(
    () => ({ activePath, setActivePath, clearActivePath }),
    [activePath, setActivePath, clearActivePath],
  );

  return (
    <TracePathContext.Provider value={value}>
      {children}
    </TracePathContext.Provider>
  );
}

/**
 * Read the trace-path highlight state. Must be called within a
 * {@link TracePathProvider} (mounted in the twin routes tree).
 */
export function useTracePath(): TracePathState {
  const ctx = useContext(TracePathContext);
  if (!ctx) {
    throw new Error("useTracePath must be used within a TracePathProvider");
  }
  return ctx;
}
